import { useEffect } from 'react';
import { toast } from 'sonner';
import { SESSION_ENDED_EVENT } from '@/lib/deviceSession';
import { watchSessionExpiry } from '@/lib/sessionExpiry';
import { clearStorageKeepLoginEndpoint, signedOutPath } from '@/lib/loginEndpoint';

// Login device limit: when a newer login elsewhere signs this device out,
// the next API call answers 401 session_revoked (see lib/deviceSession.js).
// An expired token does the same (401 session_expired), and lib/sessionExpiry.js
// announces it at the token's expiry time even if the page makes no calls.
// Sign out to the school's login page and, after the reload, tell the user why.
export const SIGNED_OUT_NOTICE_KEY = 'deskey_signed_out_notice';
const FALLBACK_MESSAGE = 'Your session has ended. Please sign in again.';
const NOTICE_DURATION_MS = 10000;

const showNotice = (message) => toast.info(message, { duration: NOTICE_DURATION_MS, id: 'session-ended' });

export default function SessionEndedGuard() {
  useEffect(() => {
    let pending = null;
    try {
      pending = sessionStorage.getItem(SIGNED_OUT_NOTICE_KEY);
      sessionStorage.removeItem(SIGNED_OUT_NOTICE_KEY);
    } catch { /* storage blocked */ }
    // Next tick: <Toaster> mounts after this guard and drops toasts sent
    // before it subscribes. (Not cleared on unmount — StrictMode re-runs
    // this effect and the key is already consumed.)
    if (pending) setTimeout(() => showNotice(pending), 0);

    const onEnded = (event) => {
      const message = event.detail?.message || FALLBACK_MESSAGE;
      // Another handler may already have cleared the login — just explain.
      if (!localStorage.getItem('desky_token')) { showNotice(message); return; }
      try { sessionStorage.setItem(SIGNED_OUT_NOTICE_KEY, message); } catch { /* shown nowhere */ }
      const target = signedOutPath();
      clearStorageKeepLoginEndpoint({ notifyServer: false });
      window.location.replace(target);
    };
    window.addEventListener(SESSION_ENDED_EVENT, onEnded);
    const stopExpiryWatch = watchSessionExpiry();
    return () => {
      window.removeEventListener(SESSION_ENDED_EVENT, onEnded);
      stopExpiryWatch();
    };
  }, []);
  return null;
}
